import {z} from 'zod';
import {WorkAttractiveConditionSchema} from '../company/schema';

export const ExtraItem = z.object({
  label: z.string(),
  value: z.string(),
});

export const CompanyDataSchema = z.object({
  address: z.string(),
  businessDescription: z.string(),
  capital: z.string(),
  catchPhrase: z.string(),
  companyID: z.number(),
  companyName: z.string(),
  employeesCount: z.string(),
  establishmentDate: z.string(),
  industry: z.string(),
  representativePerson: z.string(),
  webSiteURL: z.string(),
  extraItems: ExtraItem.array(),
});

export const GuideLineSchema = z.object({
  benefits: z.string(),
  contactURL: z.string(),
  educationSystem: z.string(),
  employmentType: z.string(),
  jobDescription: z.string(),
  jobType: z.string(),
  qualificationRequirements: z.string(),
  salary: z.string(),
  workLocation: z.string().optional(),
  extraItems: ExtraItem.array(),
});

export const Bunri = z.object({
  bunkei: z.number().optional(),
  rikei: z.number().optional(),
});

export const Sex = z.object({
  man: z.number().optional(),
  woman: z.number().optional(),
});

export const UnivType = z.object({
  bachelor: z.number().optional(),
  doctor: z.number().optional(),
  graduate: z.number().optional(),
});

export const University = z.object({
  name: z.string().optional(),
  count: z.number().optional(),
  rank: z.number().optional(),
});

export const Universities = University.array();

export const UsersCount = z.number();

export const FollowUsersAttributeSchema = z.object({
  bunri: Bunri,
  sex: Sex,
  univType: UnivType,
  university: Universities,
  usersCount: UsersCount,
});

export const InternCompany = z.object({
  count: z.number().optional(),
  name: z.string().optional(),
  rank: z.number().optional(),
});

export const InternCompanies = InternCompany.array();

export const Toeic = z.object({
  '701to800': z.number().optional(),
  '801to900': z.number().optional(),
  over901: z.number().optional(),
  under700: z.number().optional(),
});

export const OfferedUsersAttributeSchema = z.object({
  internCompany: InternCompanies,
  sex: Sex,
  toeic: Toeic,
  university: Universities,
  usersCount: UsersCount,
});

export const MetaDataSchema = z.object({
  companyID: z.number(),
  companyName: z.string(),
  companyPlan: z.string(),
  industryCategoryID: z.number(),
  industryCategoryName: z.string(),
  industryName: z.string(),
  logoURL: z.string().optional(),
  mainPhotoURL: z.string().optional(),
  recruitingFairRegistrationURL: z.string().optional(),
  entryURL: z.string().optional(),
  hasReports: z.boolean().optional(),
  vimeoID: z.string().optional(),
  graduationYear: z.number(),
  availableGraduationYears: z.array(z.number()).optional(),
  companyYearInfoID: z.number().optional(),
  workAttractiveConditions: z.array(WorkAttractiveConditionSchema).optional(),
});

export const TagsSchema = z.array(z.string());

export const SponsorCompaniesSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const NativeAdsSchema = z.object({
  category: z.string(),
  contentSummary: z.string(),
  favoriteCount: z.number(),
  favorited: z.boolean(),
  id: z.string(),
  publishDateTime: z.string(),
  updateDateTime: z.string(),
  sponsorCompanies: SponsorCompaniesSchema.array(),
  subCategory: z.string(),
  tags: TagsSchema,
  thumbnailURL: z.string(),
  title: z.string(),
  carouselThumbnailURL: z.string(),
  cardImageURL: z.string().optional(),
});

export const NativeAdSchema = NativeAdsSchema.array();

export type NativeAdType = z.infer<typeof NativeAdsSchema>;

export const NativeAdPostSchema = z.union([
  z.object({
    posts: NativeAdSchema,
  }),
  z.object({
    message: z.string(),
  }),
]);

export const CompanyPage = z.object({
  companyID: z.number(),
  slug: z.string(),
});

export const CompanyPageColumnPost = z.object({
  id: z.string(),
  category: z.string(),
  subCategory: z.string(),
  tags: TagsSchema,
  publishDateTime: z.string(),
  updateDateTime: z.string(),
  title: z.string(),
  contentSummary: z.string(),
  favorited: z.boolean(),
  favoriteCount: z.number(),
  companyPages: CompanyPage.array(),
  carouselThumbnailURL: z.string(),
  cardImageURL: z.string().optional(),
});

export type CompanyPageColumnPostType = z.infer<typeof CompanyPageColumnPost>;

export const CompanyPageColumnPosts = CompanyPageColumnPost.array().optional();

export const CompanyPageColumnPostsSchema = z.object({
  posts: CompanyPageColumnPosts,
});

export const TypeSchema = z.object({
  key: z.string(),
  label: z.string(),
});

export const ApplyMethodSchema = z.object({
  id: z.number(),
  label: z.string(),
});

export const RecruitmentSchedulesScheme = z.object({
  id: z.number(),
  name: z.string(),
  place: z.string(),
  displayOrder: z.number(),
  calendarRegisteredCount: z.number(),
  isCalendarRegistered: z.boolean(),
  applyMethod: ApplyMethodSchema,
  entryStart: z.string(),
  entryEnd: z.string(),
  eventStart: z.string(),
  eventEnd: z.string(),
  isEntryStartAllDay: z.boolean(),
  isEntryEndAllDay: z.boolean(),
  isEventStartAllDay: z.boolean(),
  isEventEndAllDay: z.boolean(),
});

export const RecruitmentTypeSchema = z.object({
  key: z.enum(['intern', 'selection', 'event', 'jointEvent', 'gssktEvent', '']),
  label: z.enum([
    'インターン',
    '本選考',
    'イベント',
    '合同イベント',
    '外就イベント',
    'etc',
  ]),
});

export const RecruitmentTagSchema = z.object({
  id: z.number(),
  longName: z.string(),
  shortName: z.string(),
  displayOrder: z.number(),
});

export const GroupedJobTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const RecruitmentCompanySchema = z.object({
  id: z.number(),
  name: z.string(),
  industryCategoryID: z.number(),
  industry: z.string(),
  foundedYear: z.number(),
  employeeCount: z.number(),
  followerCount: z.number(),
  isFollowed: z.boolean(),
  isWishedIndustry: z.boolean(),
  isWishedJobType: z.boolean(),
  isNew: z.boolean(),
  catchPhrase: z.string(),
  capitalDescription: z.string(),
  pageType: z.string(),
  backgroundImageURLs: z.object({
    '300x126': z.string(),
    '640x268': z.string(),
    '948x392': z.string(),
  }),
  logoURLs: z.object({
    '140x105': z.string(),
    '200x150': z.string(),
    '400x300': z.string(),
  }),
  studyUrl: z.string(),
});

export const RecruitmentSchema = z.object({
  id: z.number(),
  detailID: z.number(),
  title: z.string(),
  category: z.string(),
  type: RecruitmentTypeSchema,
  targetYear: z.number(),
  publishedAt: z.string(),
  postedAt: z.string(),
  displayEndAt: z.string(),
  schedules: RecruitmentSchedulesScheme.array(),
  scheduleWarning: z.object({
    context: z.string(),
  }),
  tags: RecruitmentTagSchema.array(),
  groupedJobTypes: GroupedJobTypeSchema.array(),
  companies: RecruitmentCompanySchema.array(),
  _inactiveSchedules: z.any(), // TODO エンドポイントのレスポンスの型が確定したら修正する
  entryURL: z.string(),
  detailPageURL: z.string(),
});

export const RecruitmentsSchema = RecruitmentSchema.array();

export type RecruitmentsType = z.infer<typeof RecruitmentsSchema>;

export const Recruitment = z.object({
  recruitments: RecruitmentsSchema,
});

export const SameIndustryCompanySchema = z.object({
  id: z.number(),
  logoURL: z.string().optional(),
  name: z.string(),
  industry: z.string(),
  foundedYear: z.number(),
  employeeCount: z.number(),
  followerCount: z.number(),
  followed: z.boolean(),
  detailURL: z.string(),
});

export const SameIndustryCompanies = z.object({
  list: SameIndustryCompanySchema.array(),
});

export const GroupedCompany = z.object({
  id: z.number(),
  name: z.string(),
  logoURL: z.string().optional(),
  industry: z.string(),
  description: z.string(),
  hasReports: z.boolean(), // 体験記の有無
});

export type GroupedCompany = z.infer<typeof GroupedCompany>;

export const GroupCompanies = GroupedCompany.array();

export type GroupCompanies = z.infer<typeof GroupCompanies>;

export const OfficeGalleryPictureSchema = z.object({
  imageURL: z.string().optional(),
  imageType: z.string().optional(),
  imageSize: z.number().optional(),
  caption: z.string().optional(),
});

export const OfficeGalleryPicturesSchema =
  OfficeGalleryPictureSchema.array().optional();

export const OfficeGallerySchema = z.object({
  pictures: OfficeGalleryPicturesSchema,
});

export const ParagraphSchema = z.object({
  title: z.string(),
  description: z.string().nullable(),
  photoURL: z.string().optional(),
  photoCaption: z.string().optional(),
});

export const ParagraphsSchema = ParagraphSchema.array();

export const CompanyIntroductionSchema = z.object({
  paragraphs: ParagraphsSchema.optional(),
  description: z.string().optional(),
});

export const WebSeminar = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string(),
  isFreeListing: z.boolean(),
  isOriginal: z.boolean(),
  isPrMovie: z.boolean(),
  thumbnailURL: z.string(),
});

export const WebSeminarsSchema = WebSeminar.array();

export const WebSeminarSchema = z.object({
  webSeminars: WebSeminarsSchema,
});

export const CommentSchema = z.object({
  id: z.number().optional(),
  content: z.string().optional(),
  author: z.string().optional(),
  createdAt: z.string().optional(),
});

export const CommentsSchema = CommentSchema.array();

export const RelatedCommunitySchema = z.object({
  id: z.number().optional(),
  title: z.string().optional(),
  comments: CommentsSchema.optional(),
});

export const RelatedCommunitiesSchema = RelatedCommunitySchema.array();

export const RelatedCommunities = z.object({
  relatedCommunities: RelatedCommunitiesSchema,
});

export const InterviewQA = z.object({
  question: z.string(),
  answer: z.string(),
  answerPhotoURL: z.string().optional(),
});

export const InterviewQAs = InterviewQA.array();

export type InterviewQAType = z.infer<typeof InterviewQAs>;

export const InterviewSchema = z.object({
  department: z.string(),
  headshotURL: z.string().optional(),
  interviewProfile: z.string(),
  interviewQA: InterviewQAs,
  intervieweeName: z.string(),
  yearsOfEmployment: z.string(),
});

export type InterviewType = z.infer<typeof InterviewSchema>;

export const InterviewsSchema = InterviewSchema.array();

export type InterviewsType = z.infer<typeof InterviewsSchema>;

export const Interviews = z.object({
  interviews: InterviewSchema.array(),
});

export const CompanyDetailSchema = z.object({
  metaData: MetaDataSchema,
  companyData: CompanyDataSchema.optional(),
  recruitmentGuideline: GuideLineSchema.optional(),
  followUsersAttribute: FollowUsersAttributeSchema.optional(),
  officeGallery: OfficeGallerySchema.optional(),
  offeredUsersAttribute: OfferedUsersAttributeSchema.optional(),
  introduction: CompanyIntroductionSchema.optional(),
  webSeminar: WebSeminarSchema.optional(),
  relatedCommunity: RelatedCommunities.optional(),
  companyInterview: Interviews.optional(),
});
